import type {
  IDraftAddonGroup,
  IDraftCategory,
  IDraftEffect,
  IDraftItem,
  IDraftSize,
  IMenuDraft,
  IMenuImportTurn,
  IMenuProposal,
  TDraftEffectRecord,
} from '@/interfaces/IMenuImport';

/**
 * How an assistant proposal shows (plan §4.5, §4.6): the marks on the dish
 * rows while it is pending, and the change rows of its card. The effects are
 * computed by the server (§8.12); this module only reads them.
 */

export type TRowDecoration = 'new' | 'changed' | 'removed';

export interface IEffectLine {
  key: string;
  params: Record<string, string>;
}

export interface IEffectFormat {
  /** A price in the workspace currency («$ 8.000»). */
  money(value: number): string;
  /** «a, b y c» in the locale. */
  list(items: string[]): string;
  /** The name of a category (of the draft or created by the same proposal). */
  categoryName(id: string): string;
  /** The title of an add-on group (of the draft or created by the same proposal). */
  groupTitle(id: string): string;
}

export type TProposalFormat = Pick<IEffectFormat, 'money' | 'list'>;

/** Shown for a missing price or an empty list. */
export const EMPTY_VALUE_TEXT = '—';

// ---------------------------------------------------------------------------
// Record guards (the effect records are a union; each entity has its own field)
// ---------------------------------------------------------------------------

export function isItemRecord(
  record: TDraftEffectRecord | null
): record is IDraftItem {
  return record !== null && 'categoryId' in record;
}

export function isCategoryRecord(
  record: TDraftEffectRecord | null
): record is IDraftCategory {
  return record !== null && 'itemIds' in record;
}

function isGroupRecord(
  record: TDraftEffectRecord | null
): record is IDraftAddonGroup {
  return record !== null && 'options' in record;
}

/** The index the server adds to a category record whose position changed (§8.12). */
function categoryPosition(record: IDraftCategory): number | null {
  return 'position' in record && typeof record.position === 'number'
    ? record.position
    : null;
}

// ---------------------------------------------------------------------------
// Visible changes
// ---------------------------------------------------------------------------

type TItemChange =
  | 'name'
  | 'price'
  | 'description'
  | 'category'
  | 'addons'
  | 'hidden';

function sameSizes(a: IDraftSize[], b: IDraftSize[]): boolean {
  return (
    a.length === b.length &&
    a.every((size, i) => size.label === b[i].label && size.price === b[i].price)
  );
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** What the owner would notice on a dish; review marks, flags and ids are not changes. */
function itemChanges(before: IDraftItem, after: IDraftItem): TItemChange[] {
  const changes: TItemChange[] = [];
  if (before.name !== after.name) changes.push('name');
  if (before.price !== after.price || !sameSizes(before.sizes, after.sizes))
    changes.push('price');
  if ((before.description ?? '') !== (after.description ?? ''))
    changes.push('description');
  if (before.categoryId !== after.categoryId) changes.push('category');
  if (!sameIds(before.addonGroupIds, after.addonGroupIds))
    changes.push('addons');
  if (before.hidden !== after.hidden) changes.push('hidden');
  return changes;
}

function groupChanged(
  before: IDraftAddonGroup,
  after: IDraftAddonGroup
): boolean {
  return (
    before.title !== after.title ||
    before.kind !== after.kind ||
    before.required !== after.required ||
    before.multiple !== after.multiple ||
    before.min !== after.min ||
    before.max !== after.max ||
    before.options.length !== after.options.length ||
    before.options.some(
      (option, i) =>
        option.name !== after.options[i].name ||
        option.price !== after.options[i].price
    )
  );
}

// ---------------------------------------------------------------------------
// Row decorations
// ---------------------------------------------------------------------------

/** The pending proposals of the latest finished turn (the only ones marked on the list). */
export function latestPendingProposals(
  turns: IMenuImportTurn[]
): IMenuProposal[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].status === 'done')
      return turns[i].proposals.filter(
        (proposal) => proposal.status === 'pending'
      );
  }
  return [];
}

const DECORATION_RANK: Record<TRowDecoration, number> = {
  new: 1,
  changed: 2,
  removed: 3,
};

function itemDecoration(effect: IDraftEffect): TRowDecoration | null {
  if (effect.change === 'add') return 'new';
  if (effect.change === 'remove') return 'removed';
  return isItemRecord(effect.before) &&
    isItemRecord(effect.after) &&
    itemChanges(effect.before, effect.after).length > 0
    ? 'changed'
    : null;
}

/**
 * Dish id → mark for the rows of the list («Nuevo», «Cambiado», «Se quita»).
 * When two pending proposals touch the same dish, removal wins over a change,
 * and a change over an addition.
 */
export function rowDecorations(
  turns: IMenuImportTurn[]
): Record<string, TRowDecoration> {
  const decorations: Record<string, TRowDecoration> = {};
  for (const proposal of latestPendingProposals(turns)) {
    for (const effect of proposal.effects) {
      if (effect.entity !== 'item') continue;
      const decoration = itemDecoration(effect);
      if (!decoration) continue;
      const current = decorations[effect.id];
      if (!current || DECORATION_RANK[decoration] > DECORATION_RANK[current])
        decorations[effect.id] = decoration;
    }
  }
  return decorations;
}

// ---------------------------------------------------------------------------
// Card rows
// ---------------------------------------------------------------------------

function line(key: string, params: Record<string, string>): IEffectLine {
  return { key, params };
}

function sizesText(sizes: IDraftSize[], fmt: IEffectFormat): string {
  return fmt.list(
    sizes.map(
      (size) =>
        `${size.label} ${
          size.price === null ? EMPTY_VALUE_TEXT : fmt.money(size.price)
        }`
    )
  );
}

/** A dish's price as one text: its sizes, its price or «—». */
function priceText(item: IDraftItem, fmt: IEffectFormat): string {
  if (item.sizes.length > 0) return sizesText(item.sizes, fmt);
  return item.price === null ? EMPTY_VALUE_TEXT : fmt.money(item.price);
}

function itemUpdateLines(
  before: IDraftItem,
  after: IDraftItem,
  fmt: IEffectFormat
): IEffectLine[] {
  const name = after.name;
  const lines: IEffectLine[] = [];
  for (const change of itemChanges(before, after)) {
    switch (change) {
      case 'name':
        lines.push(
          line('menuImport.proposal.row', {
            name: before.name,
            before: before.name,
            after: after.name,
          })
        );
        break;
      case 'price':
        lines.push(
          after.sizes.length > 0
            ? line('menuImport.proposal.sizesChanged', {
                name,
                list: sizesText(after.sizes, fmt),
              })
            : line('menuImport.proposal.row', {
                name,
                before: priceText(before, fmt),
                after: priceText(after, fmt),
              })
        );
        break;
      case 'description':
        lines.push(line('menuImport.proposal.descriptionChanged', { name }));
        break;
      case 'category':
        lines.push(
          line('menuImport.proposal.movedTo', {
            name,
            category: fmt.categoryName(after.categoryId),
          })
        );
        break;
      case 'addons':
        lines.push(
          line('menuImport.proposal.addonsChanged', {
            name,
            list:
              after.addonGroupIds.length > 0
                ? fmt.list(after.addonGroupIds.map((id) => fmt.groupTitle(id)))
                : EMPTY_VALUE_TEXT,
          })
        );
        break;
      case 'hidden':
        lines.push(
          line(
            after.hidden
              ? 'menuImport.proposal.hiddenOn'
              : 'menuImport.proposal.hiddenOff',
            { name }
          )
        );
        break;
    }
  }
  return lines;
}

function itemLines(effect: IDraftEffect, fmt: IEffectFormat): IEffectLine[] {
  const { before, after } = effect;
  if (effect.change === 'add')
    return isItemRecord(after)
      ? [line('menuImport.proposal.itemAdded', { name: after.name })]
      : [];
  if (effect.change === 'remove')
    return isItemRecord(before)
      ? [line('menuImport.proposal.itemRemoved', { name: before.name })]
      : [];
  return isItemRecord(before) && isItemRecord(after)
    ? itemUpdateLines(before, after, fmt)
    : [];
}

function categoryLines(effect: IDraftEffect): IEffectLine[] {
  const { before, after } = effect;
  if (effect.change === 'add')
    return isCategoryRecord(after)
      ? [line('menuImport.proposal.categoryAdded', { name: after.name })]
      : [];
  if (effect.change === 'remove')
    return isCategoryRecord(before)
      ? [line('menuImport.proposal.categoryRemoved', { name: before.name })]
      : [];
  if (!isCategoryRecord(before) || !isCategoryRecord(after)) return [];

  // A change of its dishes only is shown by the dish rows.
  const lines: IEffectLine[] = [];
  if (before.name !== after.name)
    lines.push(
      line('menuImport.proposal.categoryRenamed', {
        before: before.name,
        after: after.name,
      })
    );
  const position = categoryPosition(after);
  if (position !== null && position !== categoryPosition(before))
    lines.push(
      line('menuImport.proposal.categoryMoved', {
        name: after.name,
        n: String(position + 1),
      })
    );
  return lines;
}

function groupLines(effect: IDraftEffect): IEffectLine[] {
  const { before, after } = effect;
  if (effect.change === 'add')
    return isGroupRecord(after)
      ? [line('menuImport.proposal.groupChanged', { name: after.title })]
      : [];
  if (effect.change === 'remove')
    return isGroupRecord(before)
      ? [line('menuImport.proposal.groupChanged', { name: before.title })]
      : [];
  return isGroupRecord(before) &&
    isGroupRecord(after) &&
    groupChanged(before, after)
    ? [line('menuImport.proposal.groupChanged', { name: after.title })]
    : [];
}

/** The change rows of one effect (none when nothing the owner would notice changed). */
export function effectLines(
  effect: IDraftEffect,
  fmt: IEffectFormat
): IEffectLine[] {
  switch (effect.entity) {
    case 'item':
      return itemLines(effect, fmt);
    case 'category':
      return categoryLines(effect);
    case 'addonGroup':
      return groupLines(effect);
    default:
      return [];
  }
}

/**
 * Every change row of a proposal card, in effect order. Category and group
 * names resolve through the proposal's own effects first (a category it
 * creates or renames), then the draft.
 */
export function proposalLines(
  proposal: IMenuProposal,
  draft: IMenuDraft,
  fmt: TProposalFormat
): IEffectLine[] {
  const categoryNames = new Map<string, string>();
  const groupTitles = new Map<string, string>();
  for (const effect of proposal.effects) {
    const record = effect.after ?? effect.before;
    if (effect.entity === 'category' && isCategoryRecord(record))
      categoryNames.set(effect.id, record.name);
    if (effect.entity === 'addonGroup' && isGroupRecord(record))
      groupTitles.set(effect.id, record.title);
  }

  const format: IEffectFormat = {
    money: fmt.money,
    list: fmt.list,
    categoryName: (id) =>
      categoryNames.get(id) ?? draft.categories[id]?.name ?? EMPTY_VALUE_TEXT,
    groupTitle: (id) =>
      groupTitles.get(id) ?? draft.addonGroups[id]?.title ?? EMPTY_VALUE_TEXT,
  };
  return proposal.effects.flatMap((effect) => effectLines(effect, format));
}
